import { Link } from "react-router-dom"
import Tooltip from "@mui/material/Tooltip"
import { Typography } from "@mui/material"
import CardContent from "@mui/material/CardContent"
import Stack from "@mui/material/Stack"
import Divider from "@mui/material/Divider"

import { useTranslation } from "react-i18next"

import useRoutes from "helpers/hooks/useRoutes"
import { Card } from "widgets/styled/containers"
import CustomLink from "widgets/common/CustomLink"
import type { TowerWithDevices } from "types/towers.types"
import { formatDecimalValues } from "helpers/utils/common"
import { nameOrUnnamed } from "helpers/utils/translations"
import EntityIcon from "widgets/common/EntityIcon"

type TowerCardProps = {
  tower: TowerWithDevices
}

const TowerCard = ({ tower }: TowerCardProps) => {
  const { baseOrgURL } = useRoutes()
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  return (
    <>
      <Card
        id="twCard"
        elevation={2}
        sx={{
          border: (theme) => `1px solid ${theme.palette.neutral[400]}`,
          padding: 0,
          display: "flex",
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            p: "1rem",
          }}
        >
          <Stack spacing={2} justifyContent="space-between" height="100%">
            <Stack
              spacing={1}
              divider={<Divider flexItem sx={{ borderColor: "neutral.400" }} />}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EntityIcon entity="tower" variant="avatar" size={16} />
                <CustomLink
                  variant="h6"
                  href={`${baseOrgURL}/towers/${tower.id}`}
                  hover
                  bold
                  styles={{ fontStyle: tower.name ? "normal" : "italic" }}
                >
                  {nameOrUnnamed(tower.name, t)}
                </CustomLink>
              </Stack>
              <Stack flexDirection="row" justifyContent="space-between">
                <Typography
                  variant="body1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {t("towers.TYPE")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {tower.type ?? "-"}
                </Typography>
              </Stack>
              <Stack flexDirection="row" justifyContent="space-between">
                <Typography
                  variant="body1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {t("towers.HEIGHT")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {tower.height ? formatDecimalValues(tower.height, locale) : "-"}
                </Typography>
              </Stack>
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="body1"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {t("entities.DEVICES")}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {tower.devices.map((device) => (
                    <Tooltip
                      key={device.id}
                      title={
                        <>
                          <div>ID: {device.id} </div>
                          <div>Name: {device.name}</div>
                        </>
                      }
                      arrow
                    >
                      <Link
                        to={`${baseOrgURL}/devices/${device.id}`}
                        style={{
                          display: "inline-flex",
                          cursor: "pointer",
                          textDecoration: "none",
                        }}
                      >
                        <EntityIcon entity="device" variant="avatar" size={18} />
                      </Link>
                    </Tooltip>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  )
}

export default TowerCard
